/**
 * Resolve the orgId to attach as `X-Curium-Org-Id` on outgoing tRPC requests.
 *
 * Priority order, in keeping with the request → bootstrap → hydrated → URL fallback chain:
 *   1. `orgIdOverride` — captured when long-lived work starts so a later org switch cannot retarget it.
 *   2. `pendingOrgId` — set transiently during login bootstrap and org-switch transitions,
 *      before the store's `org` field has updated. This is the only thing in scope during
 *      `loadUser`/`loadOrg` calls inside `login()` and `changeOrg()`.
 *   3. `storeOrgId` — the hydrated authenticated state.
 *   4. `urlOrgId` — for the brief window after hard-refresh before the store rehydrates.
 */
export function resolveRequestOrgId(opts: {
    orgIdOverride?: bigint;
    pendingOrgId: bigint | undefined;
    storeOrgId: bigint | undefined;
    urlOrgId: bigint | undefined;
}): bigint | undefined {
    return opts.orgIdOverride ?? opts.pendingOrgId ?? opts.storeOrgId ?? opts.urlOrgId;
}

/**
 * Reads orgId from the current `/console/$orgId/...` URL as a fallback for the
 * brief window after a hard refresh when the auth store has not yet rehydrated.
 */
export function readOrgIdFromUrl(pathname: string): bigint | undefined {
    const match = pathname.match(/^\/console\/(-?\d+)\b/);
    if (!match) return undefined;
    try {
        return BigInt(match[1]);
    } catch {
        return undefined;
    }
}
